import { useEffect, useState } from 'react';
import { demeterApi } from '../../../../../Apis/Apis';
import { InsuranceState } from '../../../../../Generated/Raven-Demeter';
import useApi from '../../../../Apis/Hooks/useApiHook';
import useApiWithoutAutoExecute from '../../../../Apis/Hooks/useApiWithoutAutoExecute';
import PageLoadingSpinner from '../../../../Components/LoadingSpinner/PageLoadingSpinner';
import formattingService from '../../../../Services/Formatting/FormattingService';
import {
    DairyRevenueProtectionCalculationInputs,
    DairySelectionTableRow,
    defaultButterFatPercentage,
    defaultClass3MilkPercent,
    defaultClass4MilkPercent,
    defaultCoverageLevelPercent,
    defaultDeclaredComponentPriceWeighting,
    defaultDeclaredMilkProduction,
    defaultDeclaredSharePercent,
    defaultProteinPercentage,
    protectionFactorOptions,
} from './DairyRevenueProtectionDefinitions';
import DairyRevenueProtectionForm from './DairyRevenueProtectionForm';
import DairyRevenueProtectionPremiumInformation from './DairyRevenueProtectionPremiumInformation';

const defaultInsuranceState = InsuranceState.Alabama;
const defaultInsuredSharedPercentage = 100;
const defaultClassBasedPercent = 100;
const defaultComponentBasedPercent = 0;

const DairyRevenueProtection: React.FC = () => {
    // Display hooks.
    const [tableCalculations, setTableCalculations] = useState<boolean>(false);
    const [calculationInputs, setCalculatorInputs] = useState<DairyRevenueProtectionCalculationInputs>();
    const [calculatedTableRows, setCalculatedTableRows] = useState<DairySelectionTableRow[]>([]);

    const [, refreshListDairyInsurancePrices, listDairyInsurancePricesResponse] = useApiWithoutAutoExecute(() =>
        demeterApi.listDairyInsurancePrices(calculationInputs!.insuranceYear, calculationInputs!.coverageEffectiveDate, calculationInputs!.insuranceState),
    );

    const [, , listDairyInsurancePriceDatesResponse] = useApi(() => demeterApi.listDairyInsurancePriceDates());

    const sortedListDairyInsurancePriceDatesResponse = {
        ...listDairyInsurancePriceDatesResponse,
        rows: listDairyInsurancePriceDatesResponse
            ?.rows!.sort((a, b) => b.reinsuranceYear - a.reinsuranceYear)
            .map((x) => ({
                ...x,
                asOfDates: x.asOfDates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime()),
            })),
    };

    useEffect(() => {
        if (calculationInputs || !sortedListDairyInsurancePriceDatesResponse?.rows) {
            return;
        }

        setCalculatorInputs({
            insuranceYear: sortedListDairyInsurancePriceDatesResponse!.rows![0].reinsuranceYear,
            coverageEffectiveDate: formattingService.toApiDate(sortedListDairyInsurancePriceDatesResponse!.rows![0].asOfDates[0]),
            insuredSharedPercentage: defaultInsuredSharedPercentage,
            insuranceState: defaultInsuranceState,
            protectionFactor: protectionFactorOptions[0].value,
            classBasePercent: defaultClassBasedPercent,
            componentBasedPercent: defaultComponentBasedPercent,
            class3MilkPercent: defaultClass3MilkPercent,
            class4MilkPercent: defaultClass4MilkPercent,
            fatPercent: defaultButterFatPercentage,
            proteinPercent: defaultProteinPercentage,
            componentWeightingFactorPercent: defaultDeclaredComponentPriceWeighting,
            coverageLevelPercent: defaultCoverageLevelPercent,
            declaredSharePercent: defaultDeclaredSharePercent,
            declaredMilkProduction: defaultDeclaredMilkProduction,
        });
    }, [sortedListDairyInsurancePriceDatesResponse]);

    const loading = !calculationInputs;

    return loading ? (
        <PageLoadingSpinner />
    ) : (
        <>
            <DairyRevenueProtectionForm
                tableCalculations={tableCalculations}
                setTableCalculations={setTableCalculations}
                calculationInputs={calculationInputs}
                setCalculatedTableRows={setCalculatedTableRows}
                setCalculatorInputs={setCalculatorInputs}
                refreshListDairyInsurancePrices={refreshListDairyInsurancePrices}
                listDairyInsurancePriceDatesResponse={sortedListDairyInsurancePriceDatesResponse!}
                listDairyInsurancePricesResponse={listDairyInsurancePricesResponse!}
            />
            <DairyRevenueProtectionPremiumInformation
                calculationInputs={calculationInputs!}
                calculatedTableRows={calculatedTableRows}
                accordionOpen={tableCalculations}
            />
        </>
    );
};

export default DairyRevenueProtection;
